import { Link } from 'react-router-dom'

import {
  ArrowRightIcon,
  Button,
  ChannelAvatar,
  EmptyState,
  Link as ExternalLink,
  LockIcon,
  Paragraph,
  Text,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'

import styles from './AccessDenied.module.css'

import type { JSX } from 'react'

const SUPPORT_URL = 'https://support.buffer.com'

/**
 * The minimal channel shape {@link AccessDenied} needs to name a channel and
 * render its avatar. Kept structural (rather than `Pick<Channel, …>`) so callers
 * can pass a channel from any query selection, or a hand-built object, without
 * coupling to the generated GraphQL `Channel` type.
 */
export type AccessDeniedChannel = {
  service: ChannelType
  name: string
  avatar?: string | null
  displayName?: string | null
}

export type AccessDeniedProps = {
  /**
   * The channel the actor was denied access to, when known. When provided it is
   * named in the copy and shown with its avatar (single-channel surfaces); when
   * omitted the state falls back to a generic lock icon and copy (all-channels
   * surfaces, where no single channel is the subject).
   */
  channel?: AccessDeniedChannel
  /**
   * Optional override for the body copy. Defaults to a channel-aware message.
   * Provide this when a surface needs more specific wording (e.g. naming the
   * feature: "…access to Insights for this channel").
   */
  description?: React.ReactNode
  /**
   * Optional "go back" action. Pass the route to link to and the label to show;
   * omit to render no action (e.g. inline list states that already sit inside a
   * page with its own navigation).
   */
  action?: {
    to: string
    label: string
  }
  className?: string
}

/**
 * The shared "Access Denied" state shown whenever the frontend gates a surface
 * on a permission the actor lacks — Publish (`viewPublish`), Community
 * (`viewComments`), Insights (`viewInsights`), and any future surface.
 *
 * Before this existed, each surface rendered its own bespoke access-denied
 * `EmptyState` and they had drifted apart — different headings ("Access Denied"
 * vs "Access restricted" vs "Channel unavailable"), icons (lock vs critical vs
 * avatar), and colour variants. This component is the single source of truth for
 * that design so every surface looks the same (CORE-6013). Pair it with
 * {@link EnsureAccess} to gate a page in one wrapper.
 *
 * Visual treatment follows the most recent design (the single-channel Publish
 * state, CORE-6023): a `critical` {@link EmptyState} with the channel's avatar
 * when a channel is known, falling back to a lock icon otherwise.
 */
export const AccessDenied = ({
  channel,
  description,
  action,
  className,
}: AccessDeniedProps): JSX.Element => {
  return (
    <EmptyState
      size="large"
      variant="critical"
      className={[styles.fillHeight, className].filter(Boolean).join(' ')}
    >
      {channel ? (
        // Decorative: the description below already names the channel, so an
        // empty alt avoids assistive tech announcing the service name.
        <ChannelAvatar
          src={channel.avatar ?? undefined}
          alt=""
          channel={channel.service}
          size="medium"
        />
      ) : (
        <EmptyState.Icon>
          <LockIcon />
        </EmptyState.Icon>
      )}
      <EmptyState.Heading>Access restricted</EmptyState.Heading>
      <EmptyState.Description className={styles.description}>
        {description ?? <DefaultDescription channel={channel} />}
      </EmptyState.Description>
      {action && (
        <EmptyState.Actions>
          <Button as={Link} to={action.to} size="large">
            <ArrowRightIcon />
            {action.label}
          </Button>
        </EmptyState.Actions>
      )}
      <Paragraph className={styles.support}>
        Need help?{' '}
        <ExternalLink
          href={SUPPORT_URL}
          external
          className={styles.supportLink}
        >
          Talk to our Support Team
        </ExternalLink>
      </Paragraph>
    </EmptyState>
  )
}

const DefaultDescription = ({
  channel,
}: {
  channel?: AccessDeniedChannel
}): JSX.Element => {
  if (channel) {
    return (
      <>
        You don&apos;t have access to{' '}
        <Text weight="bold">{getChannelNameForDisplay(channel)}</Text>. Reach
        out to your organization owner to request access.
      </>
    )
  }

  // No channel subject (e.g. an all-channels surface). Stay channel-neutral so
  // the copy reads correctly wherever the component renders without a channel.
  return (
    <>
      You don&apos;t have access to this content. Reach out to your organization
      owner to request access.
    </>
  )
}
